import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      console.log('AuthContext - Login attempt for:', email);
      const response = await authAPI.loginUser({ email, password });
      console.log('AuthContext - Login response:', response);

      // Handle different response structures
      const responseData = response.data || response;

      if (!responseData.token) {
        throw new Error('No token received from server');
      }

      const userData = responseData.user || responseData;

      localStorage.setItem('token', responseData.token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      return responseData;
    } catch (error) {
      console.error('AuthContext - Login error:', error);
      throw error;
    }
  };

  const register = async (data, type = 'user') => {
    try {
      console.log('AuthContext - Register attempt with data:', data);
      const response = await authAPI.registerUser(data);
      console.log('AuthContext - Register response:', response);

      // Handle different response structures
      const responseData = response.data || response;

      // Check if the response indicates success
      // Your API returns { success: true, data: { token, user } }
      if (responseData.success === true) {
        // Extract the actual data from the response
        const responsePayload = responseData.data || responseData;

        // Check if token exists in the payload
        if (responsePayload.token) {
          const userData = responsePayload.user || responsePayload.restaurant || responsePayload;

          localStorage.setItem('token', responsePayload.token);
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);

          return responseData;
        } else {
          console.warn('Success response but no token found:', responsePayload);
          // Still return success even without token? Maybe registration worked but auto-login failed
          return responseData;
        }
      }
      // If response has token directly (alternative format)
      else if (responseData.token) {
        const userData = responseData.user || responseData.restaurant || responseData;

        localStorage.setItem('token', responseData.token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);

        return { success: true, data: responseData };
      }
      else {
        // If no success flag and no token, check for error message
        if (responseData.message) {
          throw new Error(responseData.message);
        }
        if (responseData.error) {
          throw new Error(responseData.error);
        }
        throw new Error('Registration failed - unexpected response format');
      }

    } catch (error) {
      console.error('AuthContext - Register error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Update user profile
  const updateProfile = (updatedUserData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUserData
    }));

    // Also update localStorage
    const currentUser = localStorage.getItem('user');
    if (currentUser) {
      const parsedUser = JSON.parse(currentUser);
      const updatedUser = { ...parsedUser, ...updatedUserData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const isAuthenticated = () => {
    return !!user && !!localStorage.getItem('token');
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const isRestaurantOwner = () => {
    return user?.role === 'owner' || user?.role === 'restaurant';
  };

  const isStaff = () => {
    return user?.role === 'staff';
  };

  const getRestaurantId = () => {
    return user?.restaurant_id;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated,
    hasRole,
    isRestaurantOwner,
    isStaff,
    getRestaurantId
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};